import { Button, Col, Row, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { GOALRUSH_SESSION_STATUS } from '../constants';
import { Badge, Section } from '../../FanWave/components/ui';
import SessionDrawer from './SessionDrawer';
import { fmtLength, fmtSpan, fmtTime, statusOf } from './sessionFormat';

const LIVE = [GOALRUSH_SESSION_STATUS.ACTIVE, GOALRUSH_SESSION_STATUS.PAUSED];
const isLive = (s) => LIVE.includes(s.status);

// No label keeps an empty label line, so the value lines up with its labelled neighbours.
const Stat = ({ label, value }) => (
  <Col noFlex gap="2px" style={{ minWidth: '110px' }}>
    <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '11px' }}>
      {label || '\u00A0'}
    </Text>
    <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
      {value}
    </Text>
  </Col>
);

const SessionRow = ({ session, onOpen }) => {
  const { t } = useTranslation();
  const status = statusOf(session);
  const paused = session.status === GOALRUSH_SESSION_STATUS.PAUSED;
  let when = fmtSpan(session.startedAt, session.endedAt ?? session.endsAt);
  if (session.endedAt) {
    when += ` (${fmtLength(new Date(session.endedAt) - new Date(session.startedAt))})`;
  } else if (!session.endsAt) {
    when += ` · ${t('admin.goalRush.sessions.noLimit')}`;
  }
  return (
    <Col
      noFlex
      wid="100%"
      gap="10px"
      pad="14px 16px"
      onClick={() => onOpen(session)}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = theme.GREY_40)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = theme.GREY_20)}
      style={{
        borderRadius: '12px',
        border: `1px solid ${theme.GREY_20}`,
        background: paused ? theme.GREY_10 : 'white',
        opacity: paused ? 0.55 : 1,
        cursor: 'pointer',
        transition: 'border-color 0.15s'
      }}
    >
      <Row center between noFlex wid="100%" gap="10px" style={{ flexWrap: 'wrap' }}>
        <Row center noFlex gap="8px">
          <Text b2 bold marg="0" style={{ fontSize: '14px' }}>
            {t(`admin.goalRush.sessions.kind.${session.kind}`)}
          </Text>
          <Badge variant={status.variant} dot>
            {t(`admin.goalRush.sessions.status.${status.key}`)}
          </Badge>
        </Row>
        {paused && (
          <Text c1 col={theme.GREY_70} marg="0" style={{ fontSize: '12px' }}>
            {t('admin.goalRush.sessions.pausedSince', { time: fmtTime(session.pausedAt) })}
          </Text>
        )}
      </Row>
      <Row start noFlex wid="100%" gap="18px" style={{ flexWrap: 'wrap' }}>
        <Col noFlex style={{ minWidth: '260px' }}>
          <Stat value={when} />
        </Col>
        <Stat
          label={t('admin.goalRush.sessions.kicks')}
          value={`${session.counters.kicks.toLocaleString()} / ${session.targets.kicks.toLocaleString()}`}
        />
        <Stat
          label={t('admin.goalRush.sessions.goals')}
          value={`${session.counters.goals.toLocaleString()} / ${session.targets.goals.toLocaleString()}`}
        />
        <Stat
          label={t('admin.goalRush.sessions.fans')}
          value={session.participantCount.toLocaleString()}
        />
      </Row>
    </Col>
  );
};

const GroupLabel = ({ children }) => (
  <Text
    c1
    bold
    col={theme.GREY_60}
    marg="0"
    style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' }}
  >
    {children}
  </Text>
);

/**
 * Every GoalRush session of the channel. "Live now" holds the running session and a default
 * session paused by an override (greyed), so they never sit among finished ones; "History" lists
 * the rest newest first — completed, timed out or terminated. The backend sorts by start time, and
 * a live session is always on its first page (at most one override starts after a default
 * pauses), so the split happens here. A row opens its details in a side panel (SessionDrawer).
 * Backend: GET /admin/goalrush/sessions.
 */
const SessionsSection = ({ teamId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sessions, setSessions] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  // The row last opened stays set while the panel slides closed, so it never empties mid-animation.
  const [selected, setSelected] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const openSession = useCallback((session) => {
    setSelected(session);
    setPanelOpen(true);
  }, []);

  const load = useCallback(
    async (nextPage) => {
      setLoading(true);
      const res = await Api.get(
        `/admin/goalrush/sessions?teamId=${teamId}&page=${nextPage}`,
        null,
        { silent: true }
      );
      setLoading(false);
      if (res?.data) {
        setSessions((prev) =>
          nextPage === 1 ? res.data.sessions : [...(prev ?? []), ...res.data.sessions]
        );
        setPage(nextPage);
        setHasMore(res.data.hasMore);
      } else if (res?.err) {
        dispatch({ type: 'HANDLE_RES', payload: { err: res.err } });
      }
    },
    [teamId, dispatch]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  // Running first, then the paused default it set aside.
  const live = useMemo(
    () =>
      (sessions ?? [])
        .filter(isLive)
        .sort((a, b) => LIVE.indexOf(a.status) - LIVE.indexOf(b.status)),
    [sessions]
  );
  const history = useMemo(() => (sessions ?? []).filter((s) => !isLive(s)), [sessions]);
  return (
    <Section
      title={t('admin.goalRush.sessions.title')}
      right={
        <Button
          textButton
          small
          text={t('admin.goalRush.sessions.refresh')}
          onClick={() => load(1)}
          loading={loading}
        />
      }
    >
      {sessions === null ? (
        <Text col={theme.GREY_60}>{t('admin.goalRush.loading')}</Text>
      ) : sessions.length === 0 ? (
        <Text c1 col={theme.GREY_60} marg="0">
          {t('admin.goalRush.sessions.none')}
        </Text>
      ) : (
        <Col noFlex wid="100%" gap="10px">
          {live.length > 0 && (
            <Col
              noFlex
              wid="100%"
              gap="10px"
              pad="0 0 0 12px"
              style={{ borderLeft: `3px solid ${theme.GREEN}` }}
            >
              <GroupLabel>{t('admin.goalRush.sessions.liveNow')}</GroupLabel>
              {live.map((s) => (
                <SessionRow key={s.id} session={s} onOpen={openSession} />
              ))}
            </Col>
          )}
          {live.length > 0 && history.length > 0 && (
            <Col noFlex wid="100%" pad="8px 0 0">
              <GroupLabel>{t('admin.goalRush.sessions.previous')}</GroupLabel>
            </Col>
          )}
          {history.map((s) => (
            <SessionRow key={s.id} session={s} onOpen={openSession} />
          ))}
          {hasMore && (
            <Row center wid="100%">
              <Button
                bordered
                small
                text={t('admin.goalRush.sessions.more')}
                onClick={() => load(page + 1)}
                loading={loading}
                wid="160px"
              />
            </Row>
          )}
        </Col>
      )}
      <SessionDrawer
        visible={panelOpen}
        teamId={teamId}
        session={selected}
        onClose={() => setPanelOpen(false)}
      />
    </Section>
  );
};

export default SessionsSection;
